import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, StickyNote } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, Spinner } from "@/components/common";
import { useAuth, useEvent, useLive } from "@/context/AppContext";
import { LabelChips, LabelsButton } from "@/components/inbox/FanLabels";
import { PurchasesCard } from "@/components/inbox/Purchases";
import { api, toastErr } from "@/lib/api";
import { money, fmtDateTime, isStaff } from "@/lib/format";
import { NO_AUTOCORRECT } from "@/lib/typing";

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function TotalDialog({ fan, open, onClose, onSaved }) {
  const [v, setV] = useState("");
  const [saving, setSaving] = useState(false);
  const key = useRef(uuid());
  useEffect(() => { if (open) { setV(String(fan.total_spent ?? 0)); key.current = uuid(); } }, [open, fan.total_spent]);
  const n = Number(String(v).replace(",", "."));
  const diff = Math.round((n - (fan.total_spent || 0)) * 100) / 100;
  const save = async () => {
    if (saving) return;
    setSaving(true);
    try { onSaved((await api.put(`/fans/${fan.id}/total-spent`, { total_spent: n, idempotency_key: key.current })).data); toast.success("Totale speso aggiornato"); onClose(); } catch (e) { toastErr(e); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm bg-[#0D0E14] border-[#232738]" data-testid="total-spent-dialog">
        <DialogHeader><DialogTitle>Modifica totale speso · @{fan.username}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-400">Totale attuale: <span className="num text-slate-100">{money(fan.total_spent)}</span></div>
        <Input type="number" min="0" step="0.01" value={v} onChange={(e) => setV(e.target.value)} className="bg-[#12141D] num text-lg" data-testid="total-spent-input" autoFocus />
        {!isNaN(n) && diff !== 0 && <div className={`text-sm num ${diff > 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="total-spent-diff">Differenza: {diff > 0 ? "+" : ""}{money(diff)}</div>}
        <p className="text-[11px] text-slate-500">Un aumento viene registrato come fatturato del chatter nel turno in corso.</p>
        <Button onClick={save} disabled={saving || isNaN(n) || n < 0 || diff === 0} className="bg-violet-600 hover:bg-violet-500" data-testid="total-spent-save">{saving ? "Registrazione..." : "Salva"}</Button>
      </DialogContent>
    </Dialog>
  );
}

function SharedNote({ fanId, initial, isOwner, meId }) {
  const [n, setN] = useState(initial);
  const [t, setT] = useState(initial.text || "");
  const [base, setBase] = useState(initial.text || "");
  const [notice, setNotice] = useState("");
  const [vers, setVers] = useState(false);
  const live = useRef({ t: initial.text || "", base: initial.text || "" });
  live.current = { t, base };
  const apply = (d) => { setN(d); setBase(d.text || ""); };
  const reload = async () => {
    const { data } = await api.get(`/fans/${fanId}/note`);
    const { t: cur, base: old } = live.current;  // values NOW, not when the event arrived
    if (cur === old) { setT(data.text || ""); } else if ((data.text || "") !== old) {
      const mine = cur.startsWith(old) ? cur.slice(old.length).replace(/^\n/, "") : cur;  // my unsaved lines kept under the colleague's text
      setT(`${data.text || ""}${mine ? `\n${mine}` : ""}`);
      setNotice(`La nota è stata aggiornata da un collega${isOwner && data.updated_by ? ` (${data.updated_by})` : ""}`);
    }
    apply(data);
  };
  useEvent("FAN_NOTE_UPDATED", (d) => d.fan_id === fanId && d.by_id !== meId && reload());
  const save = async () => { try { const { data } = await api.put(`/fans/${fanId}/note`, { text: t }); apply(data); setT(data.text || ""); setNotice(""); toast.success("Nota salvata"); } catch (e) { toastErr(e); } };
  const restore = async (i) => { try { const { data } = await api.post(`/fans/${fanId}/note/restore`, { index: i }); apply(data); setT(data.text || ""); setVers(false); toast.success("Versione ripristinata"); } catch (e) { toastErr(e); } };
  return (
    <>
      {notice && <div className="mb-1 text-[11px] text-amber-300" data-testid="fan-note-notice">{notice}</div>}
      <textarea {...NO_AUTOCORRECT} value={t} onChange={(e) => setT(e.target.value)} rows={5} readOnly={!n.can_edit} placeholder="Scrivi una nota sul fan (visibile solo allo staff)…" data-testid="fan-note-input"
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save(); }}
        className="w-full rounded-md border border-[#232738] bg-[#12141D] p-2 text-sm outline-none focus:border-amber-400/60" />
      {n.can_edit && <Button onClick={save} disabled={t === base} size="sm" className="w-full mt-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="fan-note-save-btn">Salva modifiche</Button>}
      {n.updated_at && <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-500" data-testid="fan-note-meta">
        <span>Ultima modifica: {isOwner && n.updated_by ? `${n.updated_by} · ` : ""}{fmtDateTime(n.updated_at)}</span>
        {isOwner && n.versions?.length > 0 && <button onClick={() => setVers(!vers)} className="text-amber-300 whitespace-nowrap shrink-0" data-testid="fan-note-versions-btn">🕘 Versioni</button>}
      </div>}
      {vers && <div className="mt-2 max-h-56 overflow-y-auto thin-scroll space-y-1.5" data-testid="fan-note-versions">
        {n.versions.map((v, i) => <div key={i} className="rounded border border-[#232738] p-1.5 text-[11px]">
          <div className="flex justify-between text-slate-500"><span>{v.by_name || "—"} · {v.at ? fmtDateTime(v.at) : ""}</span><button onClick={() => restore(i)} className="text-amber-300" data-testid={`fan-note-restore-${i}`}>Ripristina</button></div>
          <div className="whitespace-pre-wrap text-slate-300 line-clamp-4">{v.text || "(vuota)"}</div></div>)}
      </div>}
    </>
  );
}

export default function FanInsights({ convId, fanId: fanIdProp }) {
  const [fanId, setFanId] = useState(fanIdProp);
  const [f, setF] = useState(null);
  const [editTotal, setEditTotal] = useState(false);
  const { user } = useAuth();
  const canEdit = isStaff(user);
  useEffect(() => { if (!fanIdProp) api.get(`/conversations/${convId}`).then((r) => setFanId(r.data.conversation.fan_id)).catch(toastErr); }, [convId, fanIdProp]);
  const load = () => fanId && api.get(`/fans/${fanId}`).then((r) => setF(r.data)).catch(toastErr);
  useEffect(() => { load(); }, [fanId]); // eslint-disable-line
  useLive(load);
  const [labels, setLabels] = useState([]);
  const loadLabels = () => fanId && api.get(`/fans/${fanId}/labels`).then((r) => setLabels(r.data)).catch(() => {});
  useEffect(() => { loadLabels(); }, [fanId]); // eslint-disable-line
  useEvent("CLIENT_LABEL_UPDATED", (d) => d.fan_id === fanId && setLabels(d.labels));
  useEvent("LABELS_UPDATED", loadLabels);
  if (!f) return <Spinner />;
  return (
    <div className="flex-1 overflow-y-auto thin-scroll p-3" data-testid="fan-insights-panel">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Fan Insights</div>
      <div className="flex items-center gap-2.5">
        <Avatar name={`${f.first_name || ""} ${f.last_name || ""}`.trim() || f.username} size={40} />
        <div className="min-w-0"><div className="font-bold truncate" data-testid="fan-display-name">{`${f.first_name || ""} ${f.last_name || ""}`.trim() || `@${f.username}`}</div><div className="text-[11px] text-slate-400 truncate" data-testid="fan-username">@{f.username}</div><div className="text-[11px] text-slate-500 truncate">{f.creator_name} · @{f.account_username}</div></div>
      </div>
      <div className="mt-2 flex items-start gap-1" data-testid="fan-labels-section"><div className="flex-1 min-w-0"><LabelChips labels={labels} testid="fan-labels" /></div><LabelsButton fanId={fanId} labels={labels} onChange={setLabels} /></div>
      <button onClick={() => canEdit && setEditTotal(true)} disabled={!canEdit} className="w-full mt-3 rounded-lg bg-[#12141D] border border-[#232738] enabled:hover:border-violet-500/60 p-3 text-left transition-colors group" data-testid="fan-total-spent-btn">
        <div className="flex items-center justify-between text-[10px] text-slate-500 uppercase">Totale speso{canEdit && <Pencil className="h-3 w-3 text-violet-400" />}</div>
        <div className="num text-2xl text-emerald-400 font-semibold" data-testid="fan-total-spent">{money(f.total_spent)}</div>
        {f.spend_history?.[0] && <div className="text-[10px] text-slate-500 mt-0.5">Ultima modifica: {f.spend_history[0].user_name} · {fmtDateTime(f.spend_history[0].created_at)}</div>}
      </button>
      <PurchasesCard fanId={f.id} />
      <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.04] p-3" data-testid="fan-notes-section">
        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-300 mb-2"><StickyNote className="h-3.5 w-3.5" />Note interne</div>
        <SharedNote key={f.id} fanId={f.id} initial={f.note || { text: "", can_edit: canEdit }} isOwner={user.role === "owner"} meId={user.id} />
      </div>
      <TotalDialog fan={f} open={editTotal} onClose={() => setEditTotal(false)} onSaved={setF} />
    </div>
  );
}
